import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useFetch } from '@reveni-io/reveni-ui'

import { widgetDefault } from '@/configuration/pages/ConfigurationPage/ExtrasConfiguration/ShippingWidgetConfigurationPage/constants'
import { sortingValues } from '@/configuration/pages/ConfigurationPage/ExtrasConfiguration/ShippingWidgetConfigurationPage/ShippingWidgetConfiguration/SortingSelector/constants'
import configurationServices from '@/configuration/services/services'

const useShippingWidgetConfiguration = ({ storeId, setToastInfo }) => {
  const { t } = useTranslation()
  const {
    data: shippingWidgetPreferences,
    loading: gettingShippingWidgetPreferences,
    fetchData: refetchShippingWidgetPreferences,
  } = useFetch(() => configurationServices.getShippingWidgetPreferences({ storeId }), {
    doInitialRequest: true,
  })
  const { fetchData: editShippingWidgetPreferences, loading: applyingShippingWidgetPreferences } = useFetch(
    configurationServices.editShippingWidgetPreferences,
    {
      doInitialRequest: false,
    }
  )
  const [shippingWidgetBackgroundColor, setShippingWidgetBackgroundColor] = useState(
    shippingWidgetPreferences?.background_color || widgetDefault.background
  )
  const [shippingWidgetTextColor, setShippingWidgetTextColor] = useState(
    shippingWidgetPreferences?.text_color || widgetDefault.text
  )
  const [shippingWidgetActionsColor, setShippingWidgetActionsColor] = useState(
    shippingWidgetPreferences?.actions_color || widgetDefault.action
  )
  const [shippingWidgetFreeShippingBackgroundColor, setShippingWidgetFreeShippingBackgroundColor] = useState(
    shippingWidgetPreferences?.free_shipping_background_color || widgetDefault.freeShippingBackground
  )
  const [shippingWidgetFreeShippingTextColor, setShippingWidgetFreeShippingTextColor] = useState(
    shippingWidgetPreferences?.free_shipping_text_color || widgetDefault.freeShippingText
  )
  const [shippingWidgetSortingType, setShippingWidgetSortingType] = useState(
    shippingWidgetPreferences?.sorting || sortingValues.fastest
  )
  const [shippingWidgetHasChanges, setShippingWidgetHasChanges] = useState(false)

  const setInitValues = useCallback(() => {
    if (shippingWidgetPreferences) {
      setShippingWidgetBackgroundColor(shippingWidgetPreferences?.background_color || widgetDefault.background)
      setShippingWidgetTextColor(shippingWidgetPreferences?.text_color || widgetDefault.text)
      setShippingWidgetActionsColor(shippingWidgetPreferences?.actions_color || widgetDefault.action)
      setShippingWidgetFreeShippingBackgroundColor(
        shippingWidgetPreferences?.free_shipping_background_color || widgetDefault.freeShippingBackground
      )
      setShippingWidgetFreeShippingTextColor(
        shippingWidgetPreferences?.free_shipping_text_color || widgetDefault.freeShippingText
      )
      setShippingWidgetSortingType(shippingWidgetPreferences?.sorting || sortingValues.fastest)
      setShippingWidgetHasChanges(false)
    }
  }, [shippingWidgetPreferences])

  useEffect(() => {
    setInitValues()
  }, [setInitValues, shippingWidgetPreferences])

  const revertChanges = () => {
    setInitValues()
  }

  const saveShippingWidgetPreferences = async () => {
    const payload = {
      background_color: shippingWidgetBackgroundColor,
      text_color: shippingWidgetTextColor,
      actions_color: shippingWidgetActionsColor,
      free_shipping_background_color: shippingWidgetFreeShippingBackgroundColor,
      free_shipping_text_color: shippingWidgetFreeShippingTextColor,
      sorting: shippingWidgetSortingType,
    }
    await editShippingWidgetPreferences({ storeId, preferences: payload })
    setShippingWidgetHasChanges(false)
  }

  const shippingWidgetScriptInfo = `<script defer="defer" src="${import.meta.env.VITE_REVENI_SHIPPING_WIDGET}?platform=shopify&language={{ request.locale.iso_code }}&storeId=${storeId}&currency={{ localization.country.currency.iso_code }}&countryCode={{ localization.country.iso_code }}"></script>`

  const onSave = async () => {
    try {
      await saveShippingWidgetPreferences()
      setToastInfo({ show: true, message: t('Cambios guardados'), type: 'success' })
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }
  }

  return {
    shippingWidgetPreferences,
    gettingShippingWidgetPreferences,
    editShippingWidgetPreferences,
    refetchShippingWidgetPreferences,
    applyingShippingWidgetPreferences,
    shippingWidgetBackgroundColor,
    setShippingWidgetBackgroundColor: param => {
      setShippingWidgetHasChanges(true)
      setShippingWidgetBackgroundColor(param)
    },
    shippingWidgetTextColor,
    setShippingWidgetTextColor: param => {
      setShippingWidgetHasChanges(true)
      setShippingWidgetTextColor(param)
    },
    shippingWidgetActionsColor,
    setShippingWidgetActionsColor: param => {
      setShippingWidgetHasChanges(true)
      setShippingWidgetActionsColor(param)
    },
    shippingWidgetFreeShippingBackgroundColor,
    setShippingWidgetFreeShippingBackgroundColor: param => {
      setShippingWidgetHasChanges(true)
      setShippingWidgetFreeShippingBackgroundColor(param)
    },
    shippingWidgetFreeShippingTextColor,
    setShippingWidgetFreeShippingTextColor: param => {
      setShippingWidgetHasChanges(true)
      setShippingWidgetFreeShippingTextColor(param)
    },
    shippingWidgetSortingType,
    setShippingWidgetSortingType: param => {
      setShippingWidgetHasChanges(true)
      setShippingWidgetSortingType(param)
    },
    shippingWidgetHasChanges,
    setShippingWidgetHasChanges,
    saveShippingWidgetPreferences,
    revertShippingWidgetChanges: revertChanges,
    shippingWidgetScriptInfo,
    onSave,
  }
}

export default useShippingWidgetConfiguration
